import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './components/navigation/Header';
import Footer from './components/ui/Footer';
import Navbar from './components/navigation/Navbar';
import BottomNavigation from './components/navigation/BottomNavigation';
import TourProvider from './components/tours/TourProvider';
import { getKnownUserType, getUserTypeFromToken } from './components/tours/tourUserType';
import { blurFocusInsideDrawer } from './utils/navigation/blurFocusInsideDrawer';
import SidebarResizeHandle from './components/sidebars/SidebarResizeHandle';
import {
  DASHBOARD_SIDEBAR_WIDTH_PX,
  DASHBOARD_SIDEBAR_SHELL_RADIUS,
  DASHBOARD_CONTENT_EDGE_INSET_PX,
  DASHBOARD_CONTENT_SHELL_RADIUS,
  DASHBOARD_CONTENT_SIDEBAR_GAP_PX,
  DASHBOARD_SIDEBAR_VIEWPORT_HEIGHT_SX,
  DASHBOARD_SIDEBAR_WIDTH_CSS_VAR,
  DASHBOARD_SIDEBAR_PEEK_EDGE_PX,
  MILLET_DESKTOP_SIDEBAR_EXPAND_EVENT,
  MILLET_OPEN_DASHBOARD_SIDEBAR_EVENT,
  clampDashboardSidebarWidth,
  isEditableKeyboardTarget,
  publishDesktopSidebarState,
  readDashboardSidebarCollapsed,
  readDashboardSidebarWidthPx,
  writeDashboardSidebarCollapsed,
  writeDashboardSidebarWidthPx,
} from './utils/navigation/dashboardSidebarLayout';
import {
  SITE_HEADER_HEIGHT_MOBILE_PX,
  SITE_HEADER_HEIGHT_PX,
} from './components/marketplace/shared/listingDetailTheme';
import {
  isBrandInviteDetailsRoute,
  isCollaborationJourneyRoute,
} from './utils/navigation/bottomNavigationVisibility';
import MarketingFinalCTA from './pages/LandingPage/sections/marketing/MarketingFinalCTA';
import {
  FINAL_CTA_BAR_PADDING_Y,
  GuestLandingBand,
  GuestLandingDocumentFrame,
  isGuestDocumentFramePath,
} from './pages/LandingPage/styles/marketingLandingStyles';

const BrandSidebar = React.lazy(() => import('./components/sidebars/BrandSidebar'));
const InfluencerSidebar = React.lazy(() => import('./components/sidebars/InfluencerSidebar'));

const isPointInsideElement = (element, clientX, clientY) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return (
    clientX >= rect.left
    && clientX <= rect.right
    && clientY >= rect.top
    && clientY <= rect.bottom
  );
};

const isPointerOverOpenPeekMenu = (clientX, clientY) => {
  const menus = document.querySelectorAll('.MuiPopover-root, .MuiMenu-root');
  for (let i = 0; i < menus.length; i += 1) {
    const menu = menus[i];
    if (
      menu.getAttribute('aria-hidden') !== 'true'
      && isPointInsideElement(menu, clientX, clientY)
    ) {
      return true;
    }
  }
  return false;
};

const isPointerOverPeekDrawer = (clientX, clientY) => {
  const drawerPaper = document.querySelector('.MuiDrawer-paper[data-sidebar-peek-zone="true"]');
  return isPointInsideElement(drawerPaper, clientX, clientY);
};

const skipLinkSx = {
  position: 'absolute',
  // Pixel sizes. MUI treats width/height: 1 as 100%, which covers the page and
  // swallows vertical swipes on mobile.
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
  pointerEvents: 'none',
  textDecoration: 'none',
  '&:focus-visible': {
    pointerEvents: 'auto',
    width: 'auto',
    height: 'auto',
    margin: 0,
    overflow: 'visible',
    clip: 'auto',
    clipPath: 'none',
    whiteSpace: 'normal',
    left: 16,
    top: 16,
    zIndex: 2000,
    px: 2,
    py: 1,
    borderRadius: 1,
    bgcolor: 'background.paper',
    color: 'primary.main',
    boxShadow: 3,
  },
};

const INFLUENCER_DETAIL_PATH_PATTERN = /^\/marketplace\/influencers(?:\/(?:[^/]+\/)?dp\/[^/]+|\/[^/]+)$/;
const CAMPAIGN_DETAIL_PATH_PATTERN = /^\/marketplace\/campaigns(?:\/(?:[^/]+\/)?dp\/[^/]+|\/[^/]+)$/;
const BRAND_DETAIL_PATH_PATTERN = /^\/marketplace\/brands(?:\/(?:[^/]+\/)?dp\/[^/]+|\/[^/]+)$/;
const APPLY_CAMPAIGN_PATH_PATTERN = /^\/apply\/campaign\/[^/]+$/;
const BLOG_PATH_PATTERN = /^\/blog(?:\/|$)/;

function Layouts({ pageType, children, withSidebar, influencer, brand }) {
  const isDesktop = useMediaQuery('(min-width:1040px)', { noSsr: true });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(isDesktop);
  const [sidebarWidthPx, setSidebarWidthPx] = useState(readDashboardSidebarWidthPx);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(
    readDashboardSidebarCollapsed,
  );
  const [isDesktopSidebarPeeking, setIsDesktopSidebarPeeking] = useState(false);
  const isDesktopSidebarPeekingRef = useRef(false);
  const [userType, setUserType] = useState(null);
  const location = useLocation();
  const guestDocumentLayout = isGuestDocumentFramePath(location.pathname);
  const influencerDetailLayout = INFLUENCER_DETAIL_PATH_PATTERN.test(location.pathname);
  const campaignDetailLayout = CAMPAIGN_DETAIL_PATH_PATTERN.test(location.pathname);
  const brandDetailLayout = BRAND_DETAIL_PATH_PATTERN.test(location.pathname);
  const isApplyCampaignRoute = APPLY_CAMPAIGN_PATH_PATTERN.test(location.pathname);
  const isBlogRoute = BLOG_PATH_PATTERN.test(location.pathname);
  const isContactRoute = location.pathname === '/contact-us' || location.pathname === '/contact';
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const isCreatorMapRoute = location.pathname === '/creator-map'
    || location.pathname.startsWith('/creator-map/');
  const isAdminShell = userType === 'admin' || location.pathname.startsWith('/admin');
  // Listing shells use a sticky facet sidebar; overflow-x:hidden on ancestors breaks it.
  const isMarketplaceListingRoute = location.pathname === '/marketplace/influencers'
    || location.pathname === '/marketplace/campaigns'
    || location.pathname === '/marketplace';
  // Brand invite / collaboration status live outside /dashboard but need the same treatment:
  // overflow-x:hidden creates a nested vertical scrollport that stops momentum mid-page.
  // The marketing home, blog, and contact page are the same case: a long page with fixed chrome.
  const isMarketingHome = location.pathname === '/';
  const allowHorizontalOverflow = isMarketingHome
    || guestDocumentLayout
    || influencerDetailLayout
    || campaignDetailLayout
    || brandDetailLayout
    || isDashboardRoute
    || isAdminShell
    || isMarketplaceListingRoute
    || isBlogRoute
    || isContactRoute
    || isBrandInviteDetailsRoute(location.pathname)
    || isCollaborationJourneyRoute(location.pathname);
  const detailPageMainMinHeight = (campaignDetailLayout || brandDetailLayout)
    ? { xs: '2200px', md: '100vh' }
    : 0;

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(isDesktop);
  }, [isDesktop]);

  const setDesktopSidebarPeeking = useCallback((next) => {
    isDesktopSidebarPeekingRef.current = next;
    setIsDesktopSidebarPeeking(next);
  }, []);

  const collapseDesktopSidebar = useCallback(() => {
    setDesktopSidebarPeeking(false);
    setIsDesktopSidebarCollapsed(true);
    writeDashboardSidebarCollapsed(true);
  }, [setDesktopSidebarPeeking]);

  const expandDesktopSidebar = useCallback(() => {
    setDesktopSidebarPeeking(false);
    setIsDesktopSidebarCollapsed(false);
    writeDashboardSidebarCollapsed(false);
  }, [setDesktopSidebarPeeking]);

  useEffect(() => {
    if (!isDesktop || !isDesktopSidebarCollapsed) {
      setDesktopSidebarPeeking(false);
    }
  }, [isDesktop, isDesktopSidebarCollapsed, setDesktopSidebarPeeking]);

  // Peek only while the pointer is on the left window edge (or over the peeked drawer/menus).
  useEffect(() => {
    if (!withSidebar || !isDesktop || !isDesktopSidebarCollapsed) return undefined;

    const syncPeekFromPointer = (clientX, clientY) => {
      // Open only from the left window edge; once open, stay while over drawer/menus.
      const next = clientX <= DASHBOARD_SIDEBAR_PEEK_EDGE_PX
        || (isDesktopSidebarPeekingRef.current
          && (isPointerOverPeekDrawer(clientX, clientY)
            || isPointerOverOpenPeekMenu(clientX, clientY)));
      if (next !== isDesktopSidebarPeekingRef.current) {
        setDesktopSidebarPeeking(next);
      }
    };

    const onPointerMove = (event) => {
      syncPeekFromPointer(event.clientX, event.clientY);
    };

    const onPointerLeaveWindow = (event) => {
      // Leaving the document (e.g. out the left OS chrome) should hide the peek.
      if (event.relatedTarget == null) {
        setDesktopSidebarPeeking(false);
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onPointerLeaveWindow);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('mouseleave', onPointerLeaveWindow);
    };
  }, [withSidebar, isDesktop, isDesktopSidebarCollapsed, setDesktopSidebarPeeking]);

  const handleSidebarWidthChange = useCallback((nextWidth) => {
    const clamped = clampDashboardSidebarWidth(nextWidth);
    setSidebarWidthPx(clamped);
    writeDashboardSidebarWidthPx(clamped);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    if (isDesktop) return;
    // Blur before open=false so Modal's useEffect does not aria-hide a focused paper.
    blurFocusInsideDrawer();
    setIsSidebarOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (!withSidebar) return undefined;
    const openDashboardDrawer = () => {
      if (!isDesktop) {
        setIsSidebarOpen(true);
        return;
      }
      expandDesktopSidebar();
    };
    const expandFromNavbar = () => {
      expandDesktopSidebar();
    };
    window.addEventListener(MILLET_OPEN_DASHBOARD_SIDEBAR_EVENT, openDashboardDrawer);
    window.addEventListener(MILLET_DESKTOP_SIDEBAR_EXPAND_EVENT, expandFromNavbar);
    return () => {
      window.removeEventListener(MILLET_OPEN_DASHBOARD_SIDEBAR_EVENT, openDashboardDrawer);
      window.removeEventListener(MILLET_DESKTOP_SIDEBAR_EXPAND_EVENT, expandFromNavbar);
    };
  }, [withSidebar, isDesktop, expandDesktopSidebar]);

  useEffect(() => {
    if (!withSidebar || !isDesktop) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== '[') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableKeyboardTarget(event.target)) return;
      event.preventDefault();
      setIsDesktopSidebarCollapsed((prev) => {
        const next = !prev;
        writeDashboardSidebarCollapsed(next);
        if (!next) {
          setDesktopSidebarPeeking(false);
        }
        return next;
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [withSidebar, isDesktop, setDesktopSidebarPeeking]);

  useEffect(() => {
    if (!withSidebar) {
      document.documentElement.style.removeProperty(DASHBOARD_SIDEBAR_WIDTH_CSS_VAR);
      return undefined;
    }
    const offsetPx = isDesktop && !isDesktopSidebarCollapsed ? sidebarWidthPx : 0;
    document.documentElement.style.setProperty(
      DASHBOARD_SIDEBAR_WIDTH_CSS_VAR,
      `${offsetPx}px`,
    );
    publishDesktopSidebarState({
      collapsed: isDesktop ? isDesktopSidebarCollapsed : false,
      widthPx: sidebarWidthPx,
    });
    return () => {
      document.documentElement.style.removeProperty(DASHBOARD_SIDEBAR_WIDTH_CSS_VAR);
    };
  }, [withSidebar, isDesktop, isDesktopSidebarCollapsed, sidebarWidthPx]);

  // Safety net for closes that skip closeMobileSidebar (e.g. sidebar setIsOpen(false)).
  // useLayoutEffect runs before Modal's useEffect that applies aria-hidden.
  useLayoutEffect(() => {
    if (!withSidebar || isDesktop || isSidebarOpen) return;
    blurFocusInsideDrawer();
  }, [isDesktop, isSidebarOpen, withSidebar]);

  const handleCloseSidebarFromOutside = () => {
    if (withSidebar && isSidebarOpen) {
      closeMobileSidebar();
    }
  };

  const excludeFooterRoutes = [
    '/login',
    '/signup',
    '/sign-up',
    '/mfa-verification',
    '/forgot-password',
    '/reset-password',
    '/creator-map',
  ];

  const isResetPasswordRoute = location.pathname.startsWith('/reset-password/');

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      // No token, user is not authenticated
      setUserType(null);
      return;
    }

    // User has a token, determine type based on props and token payload
    if (influencer) {
      setUserType('influencer');
    } else if (brand) {
      // For brand routes, verify the user is actually a brand from their token
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          // Use the token payload if available, otherwise default to 'brand' since this is a brand route
          setUserType(payload.user_type || 'brand');
        } else {
          // Invalid token format, but since this is a brand route, assume brand
          setUserType('brand');
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        // If token decoding fails but this is a brand route, assume brand
        setUserType('brand');
      }
    } else {
      // No specific user type prop, try to get from token
      // This handles cases like marketplace where userType isn't explicitly set via props
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          setUserType(payload.user_type || null);
        } else {
          setUserType(null);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        setUserType(null);
      }
    }
  }, [location.pathname, influencer, brand]);

  const [determinedUserType, setDeterminedUserType] = useState(userType);

  useEffect(() => {
    setDeterminedUserType(getKnownUserType(userType) || getUserTypeFromToken());
  }, [userType]);

  useEffect(() => {
    if (!allowHorizontalOverflow) return undefined;
    const { documentElement } = document;
    const { body } = document;
    documentElement.style.overflowX = 'visible';
    body.style.overflowX = 'visible';
    return () => {
      documentElement.style.overflowX = '';
      body.style.overflowX = '';
    };
  }, [allowHorizontalOverflow]);

  const desktopDrawerOpen = !isDesktopSidebarCollapsed;
  const desktopSidebarVisible = desktopDrawerOpen || isDesktopSidebarPeeking;
  const drawerOpen = isDesktop ? desktopSidebarVisible : isSidebarOpen;
  const dashboardContentShell = withSidebar && isDesktop;
  let contentShellMarginLeft = `${DASHBOARD_CONTENT_EDGE_INSET_PX}px`;
  if (desktopDrawerOpen) {
    contentShellMarginLeft = `${DASHBOARD_CONTENT_SIDEBAR_GAP_PX}px`;
  }
  // Peek overlays content — keep layout offset at 0 while collapsed.
  let activeDrawerWidth = DASHBOARD_SIDEBAR_WIDTH_PX;
  if (isDesktop) {
    activeDrawerWidth = desktopDrawerOpen ? sidebarWidthPx : 0;
  }
  const drawerPaperRadius = isDesktop
    ? 0
    : `0 ${DASHBOARD_SIDEBAR_SHELL_RADIUS} ${DASHBOARD_SIDEBAR_SHELL_RADIUS} 0`;

  const layoutFrameSx = {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
  };
  if (dashboardContentShell) {
    // Keep the white panel inside the viewport so its bottom corners stay curved.
    layoutFrameSx.height = '100vh';
    layoutFrameSx.maxHeight = '100vh';
    layoutFrameSx.overflow = 'hidden';
    layoutFrameSx.bgcolor = 'background.canvas';
    layoutFrameSx['@supports (height: 100dvh)'] = {
      height: '100dvh',
      maxHeight: '100dvh',
    };
  } else {
    layoutFrameSx.minHeight = isCreatorMapRoute
      ? `calc(100dvh - ${SITE_HEADER_HEIGHT_PX}px)`
      : '100vh';
    layoutFrameSx.height = 'auto';
    layoutFrameSx.overflowX = allowHorizontalOverflow ? 'visible' : 'hidden';
    if (isCreatorMapRoute) {
      layoutFrameSx['@media (max-width:768px)'] = {
        minHeight: `calc(100dvh - ${SITE_HEADER_HEIGHT_MOBILE_PX}px)`,
      };
    }
  }

  const layoutRowSx = {
    display: 'flex',
    flexGrow: 1,
    margin: 0,
    padding: 0,
    bgcolor: 'background.paper',
  };
  if (dashboardContentShell) {
    layoutRowSx.minHeight = 0;
    layoutRowSx.height = '100%';
    layoutRowSx.overflow = 'hidden';
    layoutRowSx.bgcolor = 'background.canvas';
  } else if (isCreatorMapRoute) {
    layoutRowSx.minHeight = 0;
  }

  const contentColumnSx = {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    width: dashboardContentShell ? 'auto' : '100%',
    boxSizing: 'border-box',
    position: 'relative',
  };
  if (isCreatorMapRoute || dashboardContentShell) {
    contentColumnSx.minHeight = 0;
  }
  if (dashboardContentShell) {
    contentColumnSx.overflow = 'hidden';
    contentColumnSx.bgcolor = 'background.paper';
    contentColumnSx.border = '0.5px solid';
    contentColumnSx.borderColor = 'divider';
    contentColumnSx.transition = (muiTheme) => muiTheme.transitions.create('border-color', {
      duration: 150,
    });
    // Recolor the existing edge. A second stroke on the resize handle doubles its thickness.
    contentColumnSx['&:has([data-sidebar-resize-handle]:hover), &:has([data-sidebar-resize-handle][data-active="true"])'] = {
      borderLeftColor: (muiTheme) => muiTheme.palette.accent.main,
    };
    contentColumnSx.borderRadius = DASHBOARD_CONTENT_SHELL_RADIUS;
    contentColumnSx.mt = `${DASHBOARD_CONTENT_EDGE_INSET_PX}px`;
    contentColumnSx.mr = `${DASHBOARD_CONTENT_EDGE_INSET_PX}px`;
    contentColumnSx.mb = `${DASHBOARD_CONTENT_EDGE_INSET_PX}px`;
    contentColumnSx.ml = contentShellMarginLeft;
  }

  const mainSx = {
    flexGrow: 1,
    minWidth: 0,
    p: 0,
    width: '100%',
    boxSizing: 'border-box',
    bgcolor: 'background.paper',
  };
  if (isCreatorMapRoute) {
    mainSx.minHeight = 0;
  }
  if (dashboardContentShell) {
    mainSx.minHeight = 0;
    mainSx.overflowY = 'auto';
    mainSx.overflowX = 'auto';
    mainSx.borderBottomLeftRadius = DASHBOARD_CONTENT_SHELL_RADIUS;
    mainSx.borderBottomRightRadius = DASHBOARD_CONTENT_SHELL_RADIUS;
  } else {
    mainSx.minHeight = detailPageMainMinHeight;
    mainSx.overflowX = allowHorizontalOverflow ? 'visible' : 'hidden';
  }
  if (guestDocumentLayout) {
    mainSx.display = 'flex';
    mainSx.flexDirection = 'column';
  }
  const signedInHomeMain = location.pathname === '/'
    && isAuthenticated
    && (userType === 'brand' || userType === 'influencer');
  if (signedInHomeMain) {
    mainSx.display = 'flex';
    mainSx.flexDirection = 'column';
  }

  const pageContent = children || <Outlet />;

  return (
    <TourProvider userType={determinedUserType}>
      <Box sx={layoutFrameSx}>
        {pageType === 'header'
          && !isAdminShell
          && !location.pathname.includes('/reset-password')
          && location.pathname !== '/forgot-password'
          && <Header isAuthenticated={isAuthenticated} />}
        <Box sx={layoutRowSx}>
          {withSidebar && isDesktop && isDesktopSidebarCollapsed && (
            <Box
              data-testid="sidebar-peek-edge"
              aria-hidden
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                width: DASHBOARD_SIDEBAR_PEEK_EDGE_PX,
                zIndex: 1195,
                pointerEvents: 'none',
              }}
            />
          )}

          {withSidebar && (
          <Drawer
            variant={isDesktop ? 'persistent' : 'temporary'}
            anchor="left"
            open={drawerOpen}
            onClose={closeMobileSidebar}
            ModalProps={{
              // SidebarShell already preserves scroll position when the mobile drawer opens.
              disableScrollLock: true,
            }}
            PaperProps={{
              'data-sidebar-peek-zone': isDesktopSidebarPeeking ? 'true' : undefined,
            }}
            sx={{
              width: activeDrawerWidth,
              flexShrink: 0,
              transition: (muiTheme) => muiTheme.transitions.create('width', {
                easing: muiTheme.transitions.easing.sharp,
                duration: muiTheme.transitions.duration.enteringScreen,
              }),
              '& .MuiDrawer-paper': {
                width: isDesktop ? sidebarWidthPx : DASHBOARD_SIDEBAR_WIDTH_PX,
                boxSizing: 'border-box',
                top: 0,
                left: 0,
                zIndex: isDesktopSidebarPeeking ? 1300 : 1200,
                bgcolor: 'background.canvas',
                borderRadius: drawerPaperRadius,
                overflow: 'hidden',
                border: 'none',
                position: 'fixed',
                ...DASHBOARD_SIDEBAR_VIEWPORT_HEIGHT_SX,
                margin: 0,
                padding: 0,
                boxShadow: (muiTheme) => (
                  isDesktopSidebarPeeking
                    ? `4px 0 24px ${alpha(muiTheme.palette.text.primary, 0.12)}`
                    : 'none'
                ),
              },
            }}
          >
            <React.Suspense fallback={<Box sx={{ width: '100%', height: '100%' }} />}>
              {brand && (
                <BrandSidebar
                  isOpen={isDesktop ? desktopSidebarVisible : isSidebarOpen}
                  setIsOpen={setIsSidebarOpen}
                />
              )}
              {influencer && (
                <InfluencerSidebar
                  isOpen={isDesktop ? desktopSidebarVisible : isSidebarOpen}
                  setIsOpen={setIsSidebarOpen}
                />
              )}
            </React.Suspense>
          </Drawer>
          )}

          <Box sx={contentColumnSx}>
            {isDesktop && desktopDrawerOpen && (
              <SidebarResizeHandle
                widthPx={sidebarWidthPx}
                onWidthChange={handleSidebarWidthChange}
                onCollapse={collapseDesktopSidebar}
              />
            )}
            <Box component="a" href="#main-content" sx={skipLinkSx}>
              Skip to main content
            </Box>
            {pageType === 'navbar' && !isAdminShell && (
              <Navbar />
            )}
            <Box
              component="main"
              id="main-content"
              tabIndex={-1}
              onClick={handleCloseSidebarFromOutside}
              sx={mainSx}
            >
              {guestDocumentLayout ? (
                <>
                  <GuestLandingDocumentFrame>{pageContent}</GuestLandingDocumentFrame>
                  <GuestLandingBand
                    paddingY={`${FINAL_CTA_BAR_PADDING_Y}px`}
                    sx={{ flexShrink: 0, width: '100%' }}
                  >
                    <MarketingFinalCTA framePadding />
                  </GuestLandingBand>
                </>
              ) : pageContent}
            </Box>
          </Box>
        </Box>

        {(pageType === 'header' || pageType === 'blankWithFooter')
          && !isAdminShell
          && !excludeFooterRoutes.includes(location.pathname)
          && !isResetPasswordRoute
          && !isApplyCampaignRoute
          && <Footer />}
        {isAdminShell ? null : <BottomNavigation isAuthenticated={isAuthenticated} />}
      </Box>
    </TourProvider>
  );
}

export default Layouts;
